import { authentication } from "@microsoft/teams-js";
import {
  ErrorResponse,
  ErrorTimeout,
  User,
  UserManager,
  WebStorageStateStore,
} from "oidc-client-ts";
import { planIntentFromLocation, toPlanIntentState } from "@onetasklist/shared";
import { createDebugLogger } from "@utils/debugLog";
import { getEmbedHost, isEmbedded } from "@/embed/embedHost";
import { ensureTeamsSdkReady } from "@/embed/teamsHost";
import { currentReturnPath } from "@utils/returnPath";

const log = createDebugLogger("auth:oidc-client");

const origin = window.location.origin;
const oidcClientId = import.meta.env.VITE_OIDC_CLIENT_ID || "onetasklist_lobby";
const oidcScope =
  import.meta.env.VITE_OIDC_SCOPE ||
  "openid profile email offline_access onetasklist_api";
const callbackPath = "/oidc-callback";
/** Where the Teams sign-in popup starts, and the only route Teams opens directly. */
const teamsAuthStartPath = "/teams-auth-start";
/**
 * Set by the Teams sign-in popup on itself so that the callback page, reached
 * after a round trip through the identity provider, knows it is running in the
 * popup rather than in the tab.
 *
 * It cannot be a query parameter on the redirect URI: OAuth compares
 * redirect_uri byte for byte and only the bare callback path is registered.
 * sessionStorage is scoped to one window and survives navigating away to the
 * identity provider and back, which is exactly the lifetime needed. The tab is
 * a different window, so it never sees this flag.
 */
const teamsAuthPopupStorageKey = "otl.teamsAuthPopup";
/** Tall enough for the external identity provider buttons below the form. */
const TEAMS_POPUP_WIDTH = 600;
const TEAMS_POPUP_HEIGHT = 700;
/**
 * Window name given to the sign-in popup: the one an embedded host signs in
 * with, and the one any session renews in once its refresh token is refused.
 * The popup lands on the same callback path as the redirect flow, so this is
 * how the callback page tells the two apart. window.opener alone will not do
 * it: an app opened with target="_blank" has an opener too, and treating that
 * as a popup would post the result into an unrelated page instead of signing in.
 */
const embedSignInPopupWindowName = "onetasklist-embed-signin";
/** Tall enough for the external identity provider buttons below the form. */
const EMBED_POPUP_WIDTH = 600;
const EMBED_POPUP_HEIGHT = 700;
/** Entry points the public website links to. Each starts sign-in itself. */
export const signInEntryPaths = ["/login", "/signup"] as const;
/** Tells the Login page to greet a new user instead of a returning one. */
const signUpScreenHint = { screen_hint: "signup" };

/**
 * The browser refused the sign-in popup. Worth its own type: it is the one
 * sign-in failure the user can clear themselves, and the view says how.
 */
export class PopupBlockedError extends Error {
  constructor() {
    super("The browser blocked the sign-in popup");
    this.name = "PopupBlockedError";
  }
}

/**
 * Teams reports a popup it could not open as this reason, which arrives either
 * as an Error or as a bare string depending on the client.
 */
const isTeamsFailedToOpenWindow = (error: unknown): boolean =>
  (error instanceof Error ? error.message : String(error)).includes(
    "FailedToOpenWindow",
  );

// Renewal is driven by this class, not by oidc-client-ts's automaticSilentRenew.
// That makes one attempt as the token nears expiry and only retries a timeout,
// so waking a laptop from sleep defeats it: the attempt fires before the
// network is back, fails with "Failed to fetch", and nothing tries again.
const userManager = new UserManager({
  authority: origin,
  metadataUrl: `${origin}/.well-known/openid-configuration`,
  client_id: oidcClientId,
  redirect_uri: `${origin}${callbackPath}`,
  // Deliberately the same path as redirect_uri: it is already a registered
  // redirect URI for this client, and OAuth requires an exact match.
  popup_redirect_uri: `${origin}${callbackPath}`,
  post_logout_redirect_uri: `${origin}/`,
  response_type: "code",
  scope: oidcScope,
  automaticSilentRenew: false,
  loadUserInfo: false,
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
});

/** Renew once less than this is left — oidc-client-ts's own expiring-event lead time. */
const RENEW_AHEAD_SECONDS = 60;
/** Never hand out a token with less than this left; it could lapse in flight. */
const MIN_TOKEN_LIFETIME_SECONDS = 30;
/** Waits between attempts within one refresh: ~15s, long enough for Wi-Fi to rejoin after a wake. */
const REFRESH_RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000];
/** Gap between background renewals while the identity server stays unreachable. */
const BACKGROUND_RENEW_RETRY_MS = 30_000;

export type AccessTokenResult =
  | { kind: "token"; accessToken: string }
  /** No refresh token, or the identity server refused it. Only an interactive sign-in helps. */
  | { kind: "interactionRequired"; error?: Error }
  /** The identity server could not be reached. The session is probably fine; try again later. */
  | { kind: "unavailable" };

const TRANSIENT_OAUTH_ERRORS = new Set(["server_error", "temporarily_unavailable"]);
// oidc-client-ts reports an HTTP failure without an OAuth error body as a plain
// Error carrying the status, e.g. "Service Unavailable (503)".
const TRANSIENT_HTTP_STATUS = /\((408|429|5\d\d)\)/;
// ...and a gateway's HTML error page as a Content-Type mismatch.
const GATEWAY_ERROR_PAGE = "Invalid response Content-Type";

/**
 * True when a token refresh failed for a reason that says nothing about the
 * session: no network, a timeout, or the identity server or the gateway in
 * front of it having a bad moment. False when the server answered and refused
 * the refresh token, or for anything unrecognised — retrying those forever
 * would leave the user stuck instead of signing in again.
 */
export const isTransientTokenError = (error: unknown): boolean => {
  if (error instanceof ErrorResponse) {
    return TRANSIENT_OAUTH_ERRORS.has(error.error ?? "");
  }

  // fetch() rejects with a TypeError when the request never got a response.
  if (error instanceof ErrorTimeout || error instanceof TypeError) {
    return true;
  }

  if (error instanceof Error) {
    return (
      TRANSIENT_HTTP_STATUS.test(error.message) ||
      error.message.startsWith(GATEWAY_ERROR_PAGE)
    );
  }

  return false;
};

const hasLessThan = (user: User, seconds: number): boolean =>
  (user.expires_in ?? Number.POSITIVE_INFINITY) < seconds;

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

class OidcAuthClient {
  private refreshInFlight: Promise<AccessTokenResult> | null = null;
  private rejectedRefreshToken: string | null = null;
  private backgroundRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly silentRenewErrorCallbacks = new Set<(error: Error) => void>();

  constructor() {
    userManager.events.addAccessTokenExpiring(() => {
      log("accessTokenExpiring: renewing in the background");
      void this.renewInBackground();
    });
  }

  public isAuthFlowRoute(pathname: string = window.location.pathname): boolean {
    const normalized = pathname.toLowerCase();
    return (
      normalized === callbackPath ||
      normalized === teamsAuthStartPath ||
      (signInEntryPaths as readonly string[]).includes(normalized)
    );
  }

  public isCallbackUrl(url: string = window.location.href): boolean {
    const parsedUrl = new URL(url, origin);
    const callbackForAuth = parsedUrl.pathname.toLowerCase() === callbackPath;
    return (
      callbackForAuth &&
      parsedUrl.searchParams.has("code") &&
      parsedUrl.searchParams.has("state")
    );
  }

  /**
   * Returns an access token with at least 30 seconds left, refreshing it first
   * when needed. Never starts an interactive sign-in; callers decide what to do
   * with `interactionRequired`. After `unavailable`, renewal keeps retrying in
   * the background.
   */
  public async requestAccessToken(): Promise<AccessTokenResult> {
    const user = await userManager.getUser();

    if (user && !hasLessThan(user, MIN_TOKEN_LIFETIME_SECONDS)) {
      log("requestAccessToken: using stored token", {
        expiresAt: user.expires_at,
      });
      return { kind: "token", accessToken: user.access_token };
    }

    log("requestAccessToken: stored token missing or expiring, refreshing", {
      hasUser: !!user,
      expiresIn: user?.expires_in,
    });

    const result = await this.refresh();
    if (result.kind === "unavailable") {
      this.scheduleBackgroundRetry();
    }
    return result;
  }

  /**
   * Renews the token now if it is about to expire. For when the app resumes:
   * after a sleep, the renewal timer may have fired while the network was
   * still down, or not fired yet.
   */
  public async renewIfExpiring(): Promise<void> {
    const user = await userManager.getUser();
    if (!user?.refresh_token || !hasLessThan(user, RENEW_AHEAD_SECONDS)) {
      return;
    }

    log("renewIfExpiring: token expiring on resume, renewing", {
      expiresIn: user.expires_in,
    });
    await this.renewInBackground();
  }

  public onTokenRenewed(callback: (token: string) => void): () => void {
    const handler = (user: User | void) => {
      log("onTokenRenewed: userLoaded event fired", {
        hasAccessToken: !!user?.access_token,
      });
      if (user?.access_token) {
        callback(user.access_token);
      }
    };
    userManager.events.addUserLoaded(handler);
    return () => userManager.events.removeUserLoaded(handler);
  }

  /**
   * Raised when the identity server refuses a renewal, e.g. the refresh token
   * expired or was revoked — whoever asked for it: the background timer, an
   * API call or a SignalR negotiate. Raised once per refused refresh token.
   * Not raised for network trouble, which is retried instead.
   */
  public onSilentRenewError(callback: (error: Error) => void): () => void {
    const handler = (error: Error) => {
      log("onSilentRenewError: background renewal was refused", error);
      callback(error);
    };
    this.silentRenewErrorCallbacks.add(handler);
    return () => {
      this.silentRenewErrorCallbacks.delete(handler);
    };
  }

  private async renewInBackground(): Promise<void> {
    this.cancelBackgroundRetry();

    const result = await this.refresh();
    if (result.kind === "unavailable") {
      this.scheduleBackgroundRetry();
    }
  }

  private scheduleBackgroundRetry(): void {
    if (this.backgroundRetryTimer) {
      return;
    }

    log("scheduling background renewal retry", {
      delayMs: BACKGROUND_RENEW_RETRY_MS,
    });
    this.backgroundRetryTimer = setTimeout(() => {
      this.backgroundRetryTimer = null;
      void this.renewInBackground();
    }, BACKGROUND_RENEW_RETRY_MS);
  }

  private cancelBackgroundRetry(): void {
    if (this.backgroundRetryTimer) {
      clearTimeout(this.backgroundRetryTimer);
      this.backgroundRetryTimer = null;
    }
  }

  /**
   * One refresh at a time. API calls, the SignalR reconnect and the renewal
   * timer all ask for a token at once after a wake; they share one request
   * instead of each redeeming the same refresh token.
   */
  private refresh(): Promise<AccessTokenResult> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.refreshWithRetries().finally(() => {
        this.refreshInFlight = null;
      });
    }
    return this.refreshInFlight;
  }

  private async refreshWithRetries(): Promise<AccessTokenResult> {
    const user = await userManager.getUser();
    const refreshToken = user?.refresh_token;

    // Without a refresh token signinSilent() falls back to an iframe flow this
    // app has no silent_redirect_uri for, so don't let it try.
    if (!refreshToken || refreshToken === this.rejectedRefreshToken) {
      log("refresh: no usable refresh token, interactive sign-in required");
      return { kind: "interactionRequired" };
    }

    for (let attempt = 0; attempt <= REFRESH_RETRY_DELAYS_MS.length; attempt += 1) {
      if (attempt > 0) {
        await wait(REFRESH_RETRY_DELAYS_MS[attempt - 1]);
      }

      try {
        const renewed = await userManager.signinSilent();
        if (!renewed?.access_token) {
          return { kind: "interactionRequired" };
        }

        log("refresh: succeeded", { attempt, expiresAt: renewed.expires_at });
        return { kind: "token", accessToken: renewed.access_token };
      } catch (error) {
        if (!isTransientTokenError(error)) {
          log("refresh: identity server refused the refresh token", error);
          this.rejectedRefreshToken = refreshToken;
          const refusal = error instanceof Error ? error : new Error(String(error));
          this.silentRenewErrorCallbacks.forEach((callback) => callback(refusal));
          return { kind: "interactionRequired", error: refusal };
        }

        log("refresh: transient failure", { attempt, error });
      }
    }

    log("refresh: identity server unreachable, giving up for now");
    return { kind: "unavailable" };
  }

  /**
   * Runs in the Teams sign-in popup: marks this window as the popup, then sends
   * it to the identity provider using the client's registered redirect URI.
   *
   * The PKCE state this writes lives in the popup's own sessionStorage, which
   * is why the popup — not the tab — has to redeem the code afterwards.
   */
  public async startTeamsAuthPopupRedirect(): Promise<void> {
    log("startTeamsAuthPopupRedirect: marking popup and redirecting");
    this.markTeamsAuthPopup();
    await userManager.signinRedirect();
  }

  public async completeSigninFromCallbackUrl(url: string): Promise<User> {
    log("completeSigninFromCallbackUrl: starting signinCallback");
    const user = await userManager.signinCallback(url);
    if (!user) {
      log("completeSigninFromCallbackUrl: signinCallback returned no user");
      throw new Error("OIDC callback did not return a user session");
    }

    log("completeSigninFromCallbackUrl: signinCallback succeeded");
    return user;
  }

  public async startInteractiveSignIn(
    options: { signUp?: boolean } = {},
  ): Promise<void> {
    // Read the host synchronously and dispatch before anything is awaited: the
    // popup has to open inside the click that asked for it, and awaiting first
    // was enough for the browser to stop treating window.open as user-initiated
    // and refuse the popup outright. getEmbedHost() reads the launch URL rather
    // than probing a host SDK, so there is nothing to wait for.
    const embedHost = getEmbedHost();
    log("startInteractiveSignIn", {
      embedHost,
      signUp: !!options.signUp,
    });

    // Teams mediates its own popup, so it gets the Teams flow rather than the
    // window.open one below. The host is read from the launch URL rather than
    // probed at runtime: the answer is needed synchronously and the probe used
    // to race the first render.
    if (embedHost === "teams") {
      await this.startTeamsPopupSignIn();
      return;
    }

    // Any other embedded host frames the app, and the sign-in UI refuses to be
    // framed (it answers with frame-ancestors 'none'), so a redirect would
    // dead-end on a blank frame. The popup runs top-level on this origin
    // instead and hands the finished sign-in back to this frame.
    if (isEmbedded()) {
      await this.startEmbedPopupSignIn(options);
      return;
    }

    // The redirect replaces the page, so the deep link it interrupted rides in the OIDC state
    // and the callback page sends the user back to it.
    // The website's /billing?plan= choice rides the same way, so it survives sign-in without
    // browser storage. It decides where the user lands, so it replaces the deep link.
    const planIntentState = toPlanIntentState(
      planIntentFromLocation(window.location.pathname, window.location.search),
    );
    const returnTo = planIntentState ? undefined : currentReturnPath();
    const state = returnTo || planIntentState
      ? { ...(returnTo ? { returnTo } : {}), ...planIntentState }
      : undefined;
    const redirectArgs = {
      ...(options.signUp ? { extraQueryParams: signUpScreenHint } : {}),
      ...(state ? { state } : {}),
    };
    await userManager.signinRedirect(
      Object.keys(redirectArgs).length > 0 ? redirectArgs : undefined,
    );
  }

  /**
   * Signs an open session back in once its refresh token has been refused,
   * always in a popup. A redirect would reload the app and lose whatever the
   * user was in the middle of; the popup leaves the page alone, and the
   * renewed session reaches it through onTokenRenewed.
   *
   * Must be called from a user gesture, for the reason startInteractiveSignIn
   * gives: nothing may be awaited before the popup opens.
   */
  public async renewSessionInteractively(): Promise<void> {
    const embedHost = getEmbedHost();
    log("renewSessionInteractively", { embedHost });

    if (embedHost === "teams") {
      await this.startTeamsPopupSignIn();
      return;
    }

    await this.startEmbedPopupSignIn({});
  }

  /**
   * True when this window is the sign-in popup an embedded host or a session
   * renewal opened, rather than the app itself coming back from a redirect.
   */
  public isEmbedSignInPopup(): boolean {
    return window.name === embedSignInPopupWindowName;
  }

  /**
   * Runs in the popup: hands the callback URL to the opener, which completes
   * the code exchange against the PKCE state it still holds, then closes.
   */
  public async completeEmbedPopupSignIn(): Promise<void> {
    log("completeEmbedPopupSignIn: notifying the opener");
    await userManager.signinPopupCallback();
  }

  /**
   * Opens a top-level popup on this origin and hands the finished sign-in back
   * to this window. Used inside a host, and for any session renewal.
   *
   * Must be called from a user gesture — a popup opened from an effect is
   * blocked by the browser, and inside a host there is no address bar to
   * fall back to.
   */
  private async startEmbedPopupSignIn(
    options: { signUp?: boolean },
  ): Promise<void> {
    // Opened here rather than left to signinPopup so that a refusal is a plain
    // null we can name, instead of the "Attempted to navigate on a disposed
    // window" that oidc-client-ts throws two steps later. signinPopup reuses
    // this window: window.open on an existing name returns it rather than
    // opening a second one.
    const popup = this.openEmbedPopupWindow();
    if (!popup) {
      log("startEmbedPopupSignIn: the browser blocked the popup");
      throw new PopupBlockedError();
    }

    log("startEmbedPopupSignIn: opening the sign-in popup");
    try {
      await userManager.signinPopup({
        popupWindowTarget: embedSignInPopupWindowName,
        popupWindowFeatures: {
          width: EMBED_POPUP_WIDTH,
          height: EMBED_POPUP_HEIGHT,
        },
        ...(options.signUp ? { extraQueryParams: signUpScreenHint } : {}),
      });
    } catch (error) {
      // A blank popup left standing is worse than the failure that caused it.
      if (!popup.closed) {
        popup.close();
      }
      throw error;
    }
  }

  /**
   * about:blank under the name signinPopup will reuse, centred on the browser
   * window rather than the frame. Null when the browser refuses to open it.
   */
  private openEmbedPopupWindow(): Window | null {
    const left =
      window.screenX + Math.max(0, (window.outerWidth - EMBED_POPUP_WIDTH) / 2);
    const top =
      window.screenY +
      Math.max(0, (window.outerHeight - EMBED_POPUP_HEIGHT) / 2);
    const features = [
      `width=${EMBED_POPUP_WIDTH}`,
      `height=${EMBED_POPUP_HEIGHT}`,
      `left=${Math.round(left)}`,
      `top=${Math.round(top)}`,
    ].join(",");

    try {
      return window.open("", embedSignInPopupWindowName, features);
    } catch {
      // Some hosts throw rather than return null when they block a popup.
      return null;
    }
  }

  public async signOut(): Promise<void> {
    // Inside a host, signing out cannot be a redirect: the end-session page
    // answers with frame-ancestors 'none', so the frame would simply go blank.
    // Ending the identity-provider session would also be too broad — the popup
    // runs top-level, so it would sign the user out of the main site as well.
    // Here "sign out" means forget this embed's session; the next sign-in is a
    // fresh popup.
    if (isEmbedded()) {
      log("signOut: embedded, clearing this frame's session only");
      await userManager.removeUser();
      return;
    }

    log("signOut: starting signoutRedirect");
    await userManager.signoutRedirect();
  }

  /** True when this window is the sign-in popup Teams opened. */
  public isTeamsAuthPopup(): boolean {
    try {
      return window.sessionStorage.getItem(teamsAuthPopupStorageKey) === "1";
    } catch {
      // Private browsing, or site data blocked. The popup cannot identify
      // itself, so the callback page treats it as an ordinary redirect.
      return false;
    }
  }

  private markTeamsAuthPopup(): void {
    try {
      window.sessionStorage.setItem(teamsAuthPopupStorageKey, "1");
    } catch {
      // See isTeamsAuthPopup — nothing useful to do, and nothing worth failing
      // the sign-in over.
    }
  }

  private clearTeamsAuthPopup(): void {
    try {
      window.sessionStorage.removeItem(teamsAuthPopupStorageKey);
    } catch {
      // See markTeamsAuthPopup.
    }
  }

  /**
   * Runs in the Teams sign-in popup once the identity provider has sent it
   * back: redeems the code and hands the finished session to the tab.
   *
   * The popup redeems it rather than the tab because the PKCE state is in this
   * window's sessionStorage — Teams opens the popup itself, so it does not
   * inherit the tab's. What crosses back is therefore the serialised session,
   * not the callback URL, which the tab could not have redeemed.
   */
  public async completeTeamsPopupSignIn(): Promise<void> {
    try {
      const user = await this.completeSigninFromCallbackUrl(
        window.location.href,
      );
      await ensureTeamsSdkReady();
      log("completeTeamsPopupSignIn: handing the session back to the tab");
      authentication.notifySuccess(user.toStorageString());
    } catch (error) {
      log("completeTeamsPopupSignIn: failed", error);
      await this.notifyTeamsPopupFailure(error);
      throw error;
    } finally {
      this.clearTeamsAuthPopup();
    }
  }

  /** Best-effort: without the SDK there is no way to tell Teams anything. */
  private async notifyTeamsPopupFailure(error: unknown): Promise<void> {
    try {
      await ensureTeamsSdkReady();
      authentication.notifyFailure(
        error instanceof Error ? error.message : String(error),
      );
    } catch (notifyError) {
      log("notifyTeamsPopupFailure: could not reach Teams", notifyError);
    }
  }

  /**
   * Runs in the tab: asks Teams to open the sign-in popup and adopts the
   * session it returns.
   *
   * `authentication.authenticate` is host-mediated: it posts to the Teams
   * client, which opens the window. That does not exempt it from the popup
   * blocker — in Teams-in-a-browser the host's own window.open needs transient
   * activation, which only a real click anywhere in the ancestor chain
   * provides. So this must be reached from a user gesture, or the host fails
   * with FailedToOpenWindow. The desktop client has no blocker and would let
   * an automatic call through, which is exactly how such a bug stays hidden.
   */
  private async startTeamsPopupSignIn(): Promise<void> {
    await ensureTeamsSdkReady();

    log("startTeamsPopupSignIn: asking Teams to open the sign-in popup");
    let result: string | undefined;
    try {
      result = await authentication.authenticate({
        url: `${origin}${teamsAuthStartPath}`,
        width: TEAMS_POPUP_WIDTH,
        height: TEAMS_POPUP_HEIGHT,
      });
    } catch (error) {
      // Teams opens the window rather than this frame, but in a browser that is
      // still an ordinary window.open and the popup blocker still applies.
      // Teams reports that as FailedToOpenWindow. It is the same user-fixable
      // failure the Dataverse embed names, so it gets the same error and so the
      // same "allow pop-ups and try again" message.
      if (isTeamsFailedToOpenWindow(error)) {
        log("startTeamsPopupSignIn: Teams could not open the popup");
        throw new PopupBlockedError();
      }

      throw error;
    }

    if (!result) {
      log("startTeamsPopupSignIn: popup returned an empty result");
      throw new Error("Teams authentication popup returned an empty result");
    }

    const user = User.fromStorageString(result);
    await userManager.storeUser(user);
    // storeUser only writes; the event is what tells the rest of the app a
    // session now exists.
    await userManager.events.load(user);
    log("startTeamsPopupSignIn: session adopted from the popup");
  }
}

export const oidcAuthClient = new OidcAuthClient();
